import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApp, useAsync } from '../lib/app-context';
import { Async, Badge, Banner, Card, Empty, TextField } from '../components/ui';

/**
 * Validate → publish → version history.
 *
 * Validation runs server-side (`validate_module`), so what is shown here is exactly what
 * would block the publish — the admin never re-implements the rules, it surfaces them.
 * Drafts are allowed to be broken; live content is not.
 */
export function Publish() {
    const { moduleId } = useParams();
    const { data } = useApp();
    const state = useAsync(async () => ({
        module: await data.getModule(moduleId!),
        versions: await data.listVersions(moduleId!),
        problems: await data.validateModule(moduleId!),
    }), [moduleId]);

    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    async function publish() {
        setBusy(true); setError(null); setResult(null);
        try {
            const version = await data.publishModule(moduleId!, notes);
            setResult(`Published version ${version}.`);
            setNotes('');
            state.reload();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    }

    async function rollback(version: number) {
        if (!window.confirm(`Point live content back at version ${version}?`)) return;
        setBusy(true); setError(null);
        try {
            await data.rollbackModule(moduleId!, version);
            setResult(`Live content is now version ${version}.`);
            state.reload();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <>
            <div className="page-head">
                <div>
                    <h1>Publish</h1>
                    <p className="muted">
                        Publishing snapshots the whole module. Athletes mid-programme stay on the
                        version they started, so this never changes what someone is part-way through.
                    </p>
                </div>
            </div>

            <Async state={state}>
                {({ module, versions, problems }) => {
                    const live = versions.find((v) => v.id === module?.live_version_id);
                    return (
                        <>
                            {error && <div style={{ marginBottom: 16 }}><Banner tone="error" title="Failed" items={[error]} /></div>}
                            {result && <div style={{ marginBottom: 16 }}><Banner tone="ok" title={result} /></div>}

                            <div style={{ marginBottom: 16 }}>
                                {problems.length === 0 ? (
                                    <Banner tone="ok" title="Ready to publish — no problems found" />
                                ) : (
                                    <Banner
                                        tone="error"
                                        title={`${problems.length} problem${problems.length === 1 ? '' : 's'} must be fixed first`}
                                        items={problems}
                                    />
                                )}
                            </div>

                            <Card>
                                <div className="row">
                                    <div>
                                        <div className="eyebrow">Currently live</div>
                                        <h2>{live ? `Version ${live.version}` : 'Never published'}</h2>
                                        {live && (
                                            <p className="small muted">
                                                {new Date(live.published_at).toLocaleString()}
                                                {live.notes && ` — ${live.notes}`}
                                            </p>
                                        )}
                                    </div>
                                    <div className="spacer" />
                                    {live ? <Badge tone="teal">Published</Badge> : <Badge tone="coral">Draft only</Badge>}
                                </div>

                                <div className="stack" style={{ marginTop: 16 }}>
                                    <TextField label="Release notes (optional)" value={notes} onChange={setNotes}
                                        placeholder="What changed in this version?" />
                                    <div>
                                        <button
                                            className="btn"
                                            disabled={busy || problems.length > 0}
                                            onClick={publish}
                                        >
                                            {busy ? 'Publishing…' : 'Validate & publish'}
                                        </button>
                                        {problems.length > 0 && (
                                            <span className="small muted" style={{ marginLeft: 10 }}>
                                                Fix the problems above to enable this.
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </Card>

                            <div style={{ marginTop: 20 }}>
                                <h2 style={{ marginBottom: 10 }}>Version history</h2>
                                {versions.length === 0 ? (
                                    <Empty title="No versions yet" hint="Publishing creates the first one." />
                                ) : (
                                    <div className="list">
                                        {versions.map((version) => {
                                            const isLive = version.id === module?.live_version_id;
                                            return (
                                                <div key={version.id} className="list-row">
                                                    <Badge tone={isLive ? 'teal' : 'neutral'}>v{version.version}</Badge>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div className="small">
                                                            {new Date(version.published_at).toLocaleString()}
                                                        </div>
                                                        {version.notes && (
                                                            <div className="small muted">{version.notes}</div>
                                                        )}
                                                    </div>
                                                    {isLive ? (
                                                        <Badge tone="teal">Live</Badge>
                                                    ) : (
                                                        <button className="btn secondary small" disabled={busy}
                                                            onClick={() => rollback(version.version)}>
                                                            Make live
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    );
                }}
            </Async>
        </>
    );
}
